import { connect } from 'formik';
import PopperPanel from './PopperPanel';
import React, { FCC, PropsWithChildren, useRef } from 'react';
import { Color, ColorResult, SketchPicker } from 'react-color';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { FieldHelpersWrapper } from '../utils/formHelpers/FieldHelpersWrapper';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { hexToRgb, rgbToHsl, rgbToString } from './helpers';
import { IColorPickerProps, IInnerPickerProps, InternalColorPickerProps, IPortaledPickerProps } from './types';
import { useToggle } from './useToggle';

export const ColorPicker: FCC<InternalColorPickerProps> = (props) => {
  const { formik, disableAlpha, presetColors, width, showReset } = props;
  const { value } = resolveProps(props, formik);
  const id = createId('color-picker');

  const handleChange = (newColor: ColorResult) => {
    if (props.onChange) {
      props.onChange(props.name, newColor);
    } else if (formik && formik.setFieldValue) {
      formik.setFieldValue(props.name, newColor);
    }
  };

  const handleReset = () => {
    if (props.onReset) {
      props.onReset();
    } else if (formik) {
      const initial = formik.initialValues[props.name];
      formik.setFieldValue(props.name, initial);
      formik.setFieldTouched(props.name, false);
    }
  };

  return (
    <div qa-id={props['qa-id']}>
      <FieldHelpersWrapper id={id} props={props}>
        <InnerPicker
          id={id}
          value={value}
          handleChange={handleChange}
          disableAlpha={disableAlpha}
          presetColors={presetColors}
          width={width}
          showReset={showReset}
          onReset={handleReset}
        />
      </FieldHelpersWrapper>
    </div>
  );
};

const InlineInputGroup: FCC<any> = React.forwardRef<HTMLSpanElement, PropsWithChildren>((props, ref) => (
  <span ref={ref} className="input-group">
    {props.children}
  </span>
));
const InputGroup: FCC<any> = React.forwardRef<HTMLDivElement, PropsWithChildren>((props, ref) => (
  <div ref={ref} className="input-group">
    {props.children}
  </div>
));

export const InnerPicker: FCC<IInnerPickerProps> = ({
  handleChange,
  id,
  value,
  disableAlpha,
  presetColors,
  width = 200,
  inline = false,
  showReset = false,
  onReset,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement | HTMLSpanElement>();
  const [pickerOpen, , setOpen, setClosed] = useToggle(false);
  const InputGroupWrapper = inline ? InlineInputGroup : InputGroup;

  const targetRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  // forcing the type keeping the original logic in case something relies on it
  const color: Color = (value && value.rgb ? value.rgb : value) as Color;

  return (
    <>
      <InputGroupWrapper ref={wrapperRef}>
        <span
          className="input-group-addon"
          style={{
            background:
              value?.rgb?.a &&
              (value.rgb.a < 1
                ? `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAAXNSR0IArs4c6QAAADFJREFUOE9jZGBgEGHAD97gk2YcNYBhmIQBgWSAP52AwoAQwJvQRg1gACckQoC2gQgAIF8IscwEtKYAAAAASUVORK5CYII=)`
                : ''),
            backgroundColor: value && (value.rgb ? rgbToString(value.rgb) : value.hex ? value.hex : ''),
          }}
          onClick={setOpen}
        />
        <input
          className="form-control"
          type="text"
          style={{ display: inline ? 'inline' : '' }}
          value={value && value.hex ? value.hex : ''}
          id={id}
          onClick={setOpen}
          ref={targetRef}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const rgb = hexToRgb(e.target.value);
            handleChange({
              hex: e.target.value,
              hsl: rgbToHsl(rgb.r, rgb.g, rgb.b),
              rgb,
            });

            // noop, handled by color picker
          }}
        />
        {showReset && (
          <Button
            label="Reset"
            type="button"
            key={`${id}-color-picker-reset`}
            color="link"
            onClick={onReset}
            style={{ position: 'absolute' }}
          />
        )}
      </InputGroupWrapper>

      <span ref={containerRef}>
        <PopperPanel
          placement="bottom"
          targetRef={targetRef}
          containerRef={containerRef}
          showing={pickerOpen}
          hide={setClosed}
          initialFocus={''}
        >
          <div style={{ margin: '-8px' }}>
            <SketchPicker
              color={color}
              onChange={handleChange}
              disableAlpha={disableAlpha}
              presetColors={presetColors}
              width={`${width.toString()}px`}
            />
          </div>
        </PopperPanel>
      </span>
    </>
  );
};

export default connect<IColorPickerProps>(ColorPicker);
